
import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";

import { motion } from "framer-motion";

import {
  Layout,
  Menu,
  Drawer,
  Button,
  Typography,
  Badge,
  Dropdown,
  Tooltip,
} from "antd";

import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
  UserOutlined,
  ReadOutlined,
  CalendarOutlined,
  FileTextOutlined,
  NotificationOutlined,
  LogoutOutlined,
  SunOutlined,
  MoonOutlined,
  ArrowRightOutlined,
  BookOutlined,
  SafetyCertificateOutlined,
  BellOutlined,
} from "@ant-design/icons";

import {
  Box,
  Grid,
  Avatar,
  Chip,
} from "@mui/material";

import {
  FaGraduationCap,
} from "react-icons/fa";

import Swal from "sweetalert2";
import api from "../api/api";

import ParentAndStudentProfile from "./ParentAndStudentProfile";
import BioLinkProfile from "./BioLinkProfile";

import ParentsStudentGrades from "../pages/Students/ParentsStudentGrades";

import defaultAvatar from "./maleee.jpg";
import logo from "./zpa.png";
import dashboardBanner from "./teacherss.jpg";

import { useNavigate } from "react-router-dom";

const {
  Header,
  Sider,
  Content,
} = Layout;

const {
  Title,
  Text,
} = Typography;


// ======================================================
// EXACT SCHOOL COLOR PALETTE
// ======================================================

const COLORS = {
  navy: "#031B35",
  deepNavy: "#021426",
  navyBlue: "#062A4B",
  blue: "#063A6B",
  black: "#071018",

  gold: "#F6C944",
  goldLight: "#FFE58A",
  yellow: "#EFA81C",
  orange: "#C0750D",
  antique: "#CBA454",

  green: "#4FAE22",
  sky: "#0F6DAB",

  white: "#FFFFFF",
  softWhite: "#F7F9FC",
  gray: "#C4C7C7",

  page: "#F4F7F9",
  pureWhite: "#FFFFFF",
  text: "#142536",
  muted: "#6B7280",
  border: "#E0E7EC",
  soft: "#F7F9FA",

  shadow: "0 12px 35px rgba(6,42,75,.075)",
};


// ======================================================
// MAIN DASHBOARD
// ======================================================

const ParentsDashboard = () => {

  const navigate = useNavigate();

  // --------------------------------------------------
  // STATES
  // --------------------------------------------------

  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);

  const [activePage, setActivePage] = useState("dashboard");

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);

  const [showGrades, setShowGrades] = useState(false);

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const [notificationOpen, setNotificationOpen] = useState(false);
  const [clickedNotificationId, setClickedNotificationId] = useState(null);

  const [loadingLogout, setLoadingLogout] = useState(false);

  const [tempImage, setTempImage] = useState(null);
  const [overlay, setOverlay] = useState(false);

  const [isMobile, setIsMobile] = useState(
    window.innerWidth < 768
  );

  const token = localStorage.getItem("access");


  // --------------------------------------------------
  // THEME
  // --------------------------------------------------

  const theme = useMemo(() => ({

    background: darkMode
      ? COLORS.deepNavy
      : COLORS.page,

    sidebar: COLORS.deepNavy,

    header: darkMode
      ? COLORS.navy
      : COLORS.white,

    card: darkMode
      ? COLORS.navy
      : COLORS.white,

    text: darkMode
      ? COLORS.softWhite
      : COLORS.text,

    secondary: darkMode
      ? "#B7C5D3"
      : COLORS.muted,

    border: darkMode
      ? "#29425A"
      : COLORS.border,

    primary: COLORS.navyBlue,

    deepNavy: COLORS.deepNavy,

    blue: COLORS.blue,

    gold: COLORS.gold,

    goldLight: darkMode
      ? "#40371D"
      : "#FFF8E5",

    goldBorder: darkMode
      ? "#806B36"
      : "#EAD49A",

  }), [darkMode]);


  // --------------------------------------------------
  // RESPONSIVE SCREEN
  // --------------------------------------------------

  useEffect(() => {

    const resize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", resize);

    return () => {
      window.removeEventListener("resize", resize);
    };

  }, []);


  // --------------------------------------------------
  // FETCH USER PROFILE
  // --------------------------------------------------

  const fetchUserProfile = useCallback(async () => {

    try {

      const response = await api.get(
        "user-profile/"
      );

      setUser(response.data);

    } catch (error) {

      console.error("Profile error:", error);

    } finally {

      setLoading(false);

    }

  }, []);


  // --------------------------------------------------
  // REFRESH PROFILE
  // --------------------------------------------------

  const refreshUser = useCallback(async () => {

    try {

      const response = await api.get(
        "/user-profile/",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setUser(response.data);
      setTempImage(null);

    } catch (error) {

      console.error("Refresh profile error:", error);

      Swal.fire({
        icon: "error",
        title: "Profile Error",
        text: "Failed to load your profile.",
        confirmButtonColor: COLORS.navyBlue,
      });

    }

  }, [token]);


  // --------------------------------------------------
  // FETCH LINKED CHILDREN
  // --------------------------------------------------

  const fetchChildren = useCallback(async () => {

    try {

      const response = await api.get(
        "/admissions/my-linked-students/",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setChildren(
        Array.isArray(response.data)
          ? response.data
          : []
      );

    } catch (error) {

      console.error("Children error:", error);

    }

  }, [token]);


  // --------------------------------------------------
  // FETCH NOTIFICATIONS
  // --------------------------------------------------

  const fetchNotifications = useCallback(async () => {

    try {

      const response = await api.get(
        "api/users/notifications/",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = Array.isArray(response.data)
        ? response.data
        : [];

      const sorted = [...data].sort(
        (a, b) =>
          new Date(b.created_at) - new Date(a.created_at)
      );

      setNotifications(sorted);

      setUnreadCount(
        data.filter(item => !item.is_read).length
      );

    } catch (error) {

      console.error("Notification error:", error);

    }

  }, [token]);


  // --------------------------------------------------
  // INITIAL DATA LOAD
  // --------------------------------------------------

  useEffect(() => {

    fetchUserProfile();
    fetchChildren();
    fetchNotifications();

  }, [
    fetchUserProfile,
    fetchChildren,
    fetchNotifications,
  ]);


  // --------------------------------------------------
  // PROFILE IMAGE UPDATE
  // --------------------------------------------------

  const handleImageChange = async (e) => {

    const file = e.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {

      Swal.fire({
        icon: "warning",
        title: "Invalid File",
        text: "Please select a valid image.",
        confirmButtonColor: COLORS.navyBlue,
      });

      return;
    }

    const formData = new FormData();

    formData.append(
      "profile_image",
      file
    );

    const previewUrl = URL.createObjectURL(file);

    setTempImage(previewUrl);
    setOverlay(true);

    try {

      await api.put(
        "/user-profile/",
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setOverlay(false);

      await refreshUser();

      Swal.fire({
        icon: "success",
        title: "Profile Updated",
        text: "Your profile image has been updated successfully.",
        confirmButtonColor: COLORS.navyBlue,
      });

    } catch (error) {

      console.error("Image upload error:", error);

      setOverlay(false);
      setTempImage(null);

      Swal.fire({
        icon: "error",
        title: "Upload Failed",
        text:
          error.response?.data?.detail ||
          "Could not update your profile image.",
        confirmButtonColor: COLORS.navyBlue,
      });

    } finally {

      URL.revokeObjectURL(previewUrl);

    }

  };


  // --------------------------------------------------
  // MARK NOTIFICATION AS READ
  // --------------------------------------------------

  const markAsRead = async (id) => {

    try {

      await api.post(
        `api/users/notifications/${id}/mark_as_read/`,
        {
          is_read: true,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setNotifications(prev =>
        prev.map(item =>
          item.id === id
            ? {
                ...item,
                is_read: true,
              }
            : item
        )
      );

      setUnreadCount(prev =>
        Math.max(0, prev - 1)
      );

    } catch (error) {

      console.error("Mark notification error:", error);

    }

  };


  const handleNotificationClick = async (id) => {

    const notification = notifications.find(
      item => item.id === id
    );

    if (!notification || notification.is_read) return;

    setClickedNotificationId(id);

    await markAsRead(id);

    setClickedNotificationId(null);

  };


  // --------------------------------------------------
  // LOGOUT
  // --------------------------------------------------

  const handleLogout = async () => {

    const result = await Swal.fire({

      title: "Sign Out?",

      text: "Are you sure you want to leave your dashboard?",

      icon: "question",

      showCancelButton: true,

      confirmButtonText: "Yes, Logout",

      cancelButtonText: "Stay Here",

      confirmButtonColor: COLORS.navyBlue,

      cancelButtonColor: "#64748B",

      reverseButtons: true,

    });

    if (!result.isConfirmed) return;

    setLoadingLogout(true);

    try {

      await api.post(
        "api/logout/",
        {}
      );

    } catch (error) {

      console.error("Logout error:", error);

    } finally {

      localStorage.removeItem("access");
      localStorage.removeItem("refresh");

      setLoadingLogout(false);

      navigate("/login");

    }

  };


  // --------------------------------------------------
  // SIDEBAR MENU
  // --------------------------------------------------

  const menuItems = [

    {
      key: "dashboard",
      icon: <DashboardOutlined />,
      label: "Dashboard",
    },

    {
      key: "assignments",
      icon: <FileTextOutlined />,
      label: "Assignments",
    },

  ];


  const childMenuItems = children.map(child => ({

    key: `child_${child.id}`,

    icon: <UserOutlined />,

    label: child.full_name || "Student",

  }));


  const fullMenu = [

    ...menuItems,

    {
      type: "divider",
    },

    {
      type: "group",
      label: "My Children",
      children: childMenuItems,
    },

  ];


  // --------------------------------------------------
  // MENU CLICK
  // --------------------------------------------------

  const handleMenuClick = ({ key }) => {

    if (key.startsWith("child_")) {

      const id = key.split("_")[1];

      const child = children.find(
        item => item.id.toString() === id
      );

      if (!child) return;

      setSelectedChild(child);
      setShowGrades(false);

    }

    setActivePage(key);

    if (isMobile) {
      setDrawerOpen(false);
    }

  };


  // --------------------------------------------------
  // SIDEBAR
  // --------------------------------------------------

  const sidebar = (

    <div
      className="parent-sidebar"
      style={{
        height: "100%",
        minHeight: "100%",
        display: "flex",
        flexDirection: "column",
        background: COLORS.deepNavy,
        overflow: "hidden",
      }}
    >

      {/* SCHOOL BRAND */}

      <div
        className="sidebar-brand"
        style={{
          minHeight: 85,
          display: "flex",
          alignItems: "center",
          padding: collapsed ? "0 13px" : "0 18px",
          justifyContent: collapsed ? "center" : "flex-start",
          gap: 13,
          borderBottom: "1px solid rgba(255,255,255,.10)",
          flexShrink: 0,
        }}
      >

        <img
          src={logo}
          alt="Zion Praise Academy"
          style={{
            width: 48,
            height: 48,
            minWidth: 48,
            borderRadius: 13,
            objectFit: "cover",
            border: `2px solid ${COLORS.gold}`,
            boxShadow: "0 5px 18px rgba(0,0,0,.25)",
          }}
        />

        {!collapsed && (

          <div style={{ overflow: "hidden" }}>

            <Title
              level={5}
              style={{
                margin: 0,
                fontSize: 14,
                color: COLORS.white,
                fontWeight: 900,
                lineHeight: 1.3,
                whiteSpace: "normal",
              }}
            >
              Zion Praise Academy
            </Title>

            <Text
              style={{
                fontSize: 10,
                color: COLORS.goldLight,
                letterSpacing: ".3px",
              }}
            >
              SCHOOL MANAGEMENT PORTAL
            </Text>

          </div>

        )}

      </div>


      {/* PROFILE */}

      {!collapsed && (

        <div
          style={{
            padding: "18px 13px 12px",
            flexShrink: 0,
          }}
        >

          <div
            style={{
              background: "linear-gradient(145deg,#062A4B,#031B35)",
              borderRadius: 18,
              padding: 12,
              textAlign: "center",
              border: "1px solid rgba(246,201,68,.25)",
              boxShadow: "0 12px 30px rgba(0,0,0,.18)",
            }}
          >

            <ParentAndStudentProfile
              user={user}
              refreshUser={refreshUser}
              tempImage={tempImage}
              setTempImage={setTempImage}
              theme={theme}
              darkMode={darkMode}
            />

            {overlay && (

              <div
                style={{
                  marginTop: 8,
                  color: COLORS.goldLight,
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                Updating profile image...
              </div>

            )}

          </div>

        </div>

      )}


      {/* NAVIGATION */}

      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          padding: collapsed ? "12px 8px" : "10px 12px",
        }}
      >

        <Menu
          className="parent-menu"
          mode="inline"
          items={fullMenu}
          selectedKeys={[activePage]}
          inlineCollapsed={collapsed}
          onClick={handleMenuClick}
          theme="dark"
          style={{
            border: "none",
            background: "transparent",
            fontWeight: 600,
          }}
        />

      </div>


      {/* SIDEBAR FOOTER */}

      <div
        style={{
          minHeight: 58,
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "flex-start",
          padding: "0 17px",
          borderTop: "1px solid rgba(255,255,255,.10)",
          flexShrink: 0,
        }}
      >

        {!collapsed && (

          <Text
            style={{
              fontSize: 10,
              color: "#AAB9C9",
              fontWeight: 500,
            }}
          >
            © 2026 Zion Praise Academy
          </Text>

        )}

        {collapsed && (
          <SafetyCertificateOutlined
            style={{
              color: COLORS.gold,
              fontSize: 19,
            }}
          />
        )}

      </div>

    </div>

  );


  // --------------------------------------------------
  // MOBILE DRAWER
  // --------------------------------------------------

  const mobileSidebar = (

    <div
      style={{
        height: "100%",
        background: COLORS.deepNavy,
      }}
    >
      {sidebar}
    </div>

  );


  const sidebarWidth = collapsed ? 78 : 270;


  const drawerContent = (

    <Drawer
      open={drawerOpen}
      width={285}
      placement="left"
      onClose={() => setDrawerOpen(false)}
      closable={false}
      styles={{
        body: {
          padding: 0,
          background: COLORS.deepNavy,
          overflow: "hidden",
        },
      }}
    >

      {mobileSidebar}

    </Drawer>

  );


  // --------------------------------------------------
  // DASHBOARD FEATURE CARD
  // --------------------------------------------------

  const FeatureCard = ({
    icon,
    title,
    description,
    iconColor = COLORS.navyBlue,
    iconBackground = "#FFF5D5",
  }) => (

    <Box
      className="parent-feature-card"
      sx={{
        position: "relative",
        overflow: "hidden",
        minHeight: {
          xs: 190,
          md: 220,
        },
        p: {
          xs: 2.5,
          md: 3,
        },
        borderRadius: 4,
        background: theme.card,
        border: `1px solid ${theme.border}`,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        boxShadow: darkMode
          ? "0 10px 25px rgba(0,0,0,.12)"
          : "0 8px 25px rgba(6,42,75,.045)",
        transition: "all .32s ease",
      }}
    >

      <Box
        className="feature-icon"
        sx={{
          width: 64,
          height: 64,
          borderRadius: 3,
          background: iconBackground,
          border: `1px solid ${theme.goldBorder}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 2.2,
          zIndex: 1,
        }}
      >

        {React.cloneElement(icon, {
          style: {
            fontSize: 30,
            color: iconColor,
          },
        })}

      </Box>


      <Typography
        sx={{
          fontSize: {
            xs: 16,
            md: 18,
          },
          fontWeight: 900,
          color: theme.text,
          mb: 0.7,
          zIndex: 1,
        }}
      >
        {title}
      </Typography>


      <Typography
        sx={{
          fontSize: 13,
          lineHeight: 1.7,
          color: theme.secondary,
          maxWidth: 280,
          zIndex: 1,
        }}
      >
        {description}
      </Typography>


      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          height: 3,
          width: "100%",
          background: `linear-gradient(90deg,${COLORS.gold},transparent)`,
          opacity: 0.7,
        }}
      />

    </Box>

  );


  // ======================================================
  // RENDER
  // ======================================================

  return (

    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: theme.background,
        overflow: "hidden",
      }}
    >

      {/* ==================================================
          GLOBAL STYLING
      ================================================== */}

      <style>{`

        * {
          box-sizing: border-box;
        }

        .parent-sidebar {
          scrollbar-width: thin;
          scrollbar-color: rgba(246,201,68,.55) transparent;
        }

        .parent-sidebar::-webkit-scrollbar,
        .parent-sidebar *::-webkit-scrollbar {
          width: 5px;
        }

        .parent-sidebar::-webkit-scrollbar-thumb,
        .parent-sidebar *::-webkit-scrollbar-thumb {
          background: rgba(246,201,68,.45);
          border-radius: 10px;
        }

        .parent-sidebar::-webkit-scrollbar-track,
        .parent-sidebar *::-webkit-scrollbar-track {
          background: transparent;
        }

        /* SIDEBAR MENU */

        .parent-menu,
        .parent-menu.ant-menu-dark {
          background: transparent !important;
          border: none !important;
        }

        .parent-menu .ant-menu-item {
          height: 46px !important;
          line-height: 46px !important;
          margin: 5px 0 !important;
          width: 100% !important;
          border-radius: 10px !important;
          color: #D5E0EB !important;
          font-weight: 600 !important;
          transition: all .28s ease !important;
        }

        .parent-menu .ant-menu-item .anticon {
          font-size: 17px;
          color: #CBA454 !important;
          transition: all .25s ease;
        }

        .parent-menu .ant-menu-item:hover {
          background: rgba(246,201,68,.13) !important;
          color: #FFE58A !important;
          transform: translateX(4px);
        }

        .parent-menu .ant-menu-item:hover .anticon {
          color: #F6C944 !important;
          transform: scale(1.12);
        }

        .parent-menu .ant-menu-item-selected {
          background: linear-gradient(
            100deg,
            #F6C944,
            #EFA81C
          ) !important;

          color: #021426 !important;
          font-weight: 800 !important;

          box-shadow: 0 5px 15px rgba(246,201,68,.20);
        }

        .parent-menu .ant-menu-item-selected .anticon {
          color: #021426 !important;
        }

        .parent-menu .ant-menu-item-selected::after {
          display: none !important;
        }

        .parent-menu .ant-menu-item-group-title {
          color: #CBA454 !important;
          font-size: 11px !important;
          font-weight: 800 !important;
          text-transform: uppercase;
          letter-spacing: 1px;
          padding-top: 20px !important;
        }

        .parent-menu .ant-menu-item-divider {
          border-color: rgba(255,255,255,.13) !important;
        }

        /* DASHBOARD FEATURE CARDS */

        .parent-feature-card {
          cursor: default;
          transition: all .32s cubic-bezier(.2,.8,.2,1) !important;
        }

        .parent-feature-card:hover {
          transform: translateY(-7px);
          border-color: #F6C944 !important;
          box-shadow: 0 18px 38px rgba(3,27,53,.13) !important;
        }

        .parent-feature-card .feature-icon {
          transition: all .35s ease;
        }

        .parent-feature-card:hover .feature-icon {
          transform: scale(1.08) rotate(-4deg);
          box-shadow: 0 10px 24px rgba(246,201,68,.25);
        }

        .parent-feature-card::after {
          content: "";
          position: absolute;
          width: 130px;
          height: 130px;
          right: -65px;
          top: -65px;
          border-radius: 50%;
          background: rgba(246,201,68,.07);
          transition: all .4s ease;
          pointer-events: none;
        }

        .parent-feature-card:hover::after {
          transform: scale(1.8);
        }

        /* HEADER BUTTONS */

        .parent-header-button {
          border: 1px solid ${theme.border} !important;
          color: ${theme.text} !important;
          background: ${darkMode ? "#18324B" : "#F7F9FC"} !important;
          box-shadow: none !important;
          transition: all .25s ease !important;
        }

        .parent-header-button:hover {
          border-color: #F6C944 !important;
          color: #C0750D !important;
          transform: translateY(-2px);
        }

        /* NOTIFICATION */

        .parent-notification-item {
          transition: all .2s ease;
        }

        .parent-notification-item:hover {
          border-color: #F6C944 !important;
          transform: translateX(3px);
        }

        /* MOBILE */

        @media(max-width:767px) {
          .parent-feature-card:hover {
            transform: translateY(-3px);
          }
        }

        @media(prefers-reduced-motion:reduce) {
          .parent-sidebar *,
          .parent-feature-card,
          .parent-feature-card *,
          .parent-header-button {
            transition: none !important;
            animation: none !important;
          }
        }

      `}</style>


      <Layout
        style={{
          height: "100vh",
          background: theme.background,
        }}
      >

        {/* ==================================================
            DESKTOP SIDEBAR
        ================================================== */}

        {!isMobile && (

          <Sider
            width={270}
            collapsed={collapsed}
            collapsedWidth={78}
            trigger={null}
            style={{
              height: "100vh",
              position: "fixed",
              left: 0,
              top: 0,
              bottom: 0,
              background: COLORS.deepNavy,
              borderRight: "1px solid rgba(255,255,255,.08)",
              zIndex: 20,
              overflow: "hidden",
              boxShadow: "5px 0 25px rgba(2,20,38,.08)",
            }}
          >

            {sidebar}

          </Sider>

        )}


        {/* MOBILE SIDEBAR */}

        {isMobile && drawerContent}


        {/* ==================================================
            MAIN LAYOUT
        ================================================== */}

        <Layout
          style={{
            marginLeft: isMobile ? 0 : sidebarWidth,
            height: "100vh",
            background: theme.background,
            transition: "margin-left .25s ease",
            minWidth: 0,
          }}
        >


          {/* ==================================================
              HEADER
          ================================================== */}

          <Header
            style={{
              height: isMobile ? 65 : 75,
              minHeight: isMobile ? 65 : 75,
              padding: isMobile ? "0 10px" : "0 25px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: theme.header,
              borderBottom: `1px solid ${theme.border}`,
              position: "sticky",
              top: 0,
              zIndex: 10,
              boxShadow: "0 3px 15px rgba(3,27,53,.035)",
              overflow: "hidden",
              lineHeight: "normal",
            }}
          >

            {/* LEFT HEADER */}

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: isMobile ? 8 : 15,
                minWidth: 0,
                flexShrink: 1,
                height: "100%",
              }}
            >

              <Tooltip title={isMobile ? "Open Menu" : collapsed ? "Expand Sidebar" : "Collapse Sidebar"}>

                <Button
                  className="parent-header-button"
                  shape="circle"
                  size="large"
                  icon={
                    isMobile || collapsed
                      ? <MenuUnfoldOutlined />
                      : <MenuFoldOutlined />
                  }
                  onClick={() => {

                    if (isMobile) {
                      setDrawerOpen(true);
                    } else {
                      setCollapsed(prev => !prev);
                    }

                  }}
                />

              </Tooltip>


              {/* MOBILE BRAND */}

              {isMobile ? (

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    whiteSpace: "nowrap",
                    flexWrap: "nowrap",
                    height: 59,
                    lineHeight: 1,
                    flexShrink: 1,
                    overflow: "hidden",
                    fontFamily: "'Arial Black','Impact',sans-serif",
                    fontSize: "clamp(15px,4.8vw,23px)",
                    fontWeight: 1000,
                    letterSpacing: "-1px",
                    textTransform: "uppercase",
                  }}
                >

                  <span
                    style={{
                      color: COLORS.navy,
                      WebkitTextStroke: "0.5px #031B35",
                    }}
                  >
                    PARENT
                  </span>

                  <span
                    style={{
                      color: COLORS.orange,
                      WebkitTextStroke: "0.5px #C0750D",
                    }}
                  >
                    PORTAL
                  </span>

                </div>

              ) : (

                <div style={{ minWidth: 0 }}>

                  <Typography
                    style={{
                      fontSize: 21,
                      fontWeight: 900,
                      color: theme.text,
                      lineHeight: 1.3,
                    }}
                  >
                    Parent Dashboard
                  </Typography>

                  <Typography
                    style={{
                      fontSize: 11,
                      color: theme.secondary,
                    }}
                  >
                    Zion Praise Academy · School Management System
                  </Typography>

                </div>

              )}

            </div>


            {/* RIGHT HEADER ACTIONS */}

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: isMobile ? 6 : 12,
                flexShrink: 0,
              }}
            >

              {/* DARK MODE */}

              <Tooltip title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}>

                <Button
                  className="parent-header-button"
                  shape="circle"
                  onClick={() => setDarkMode(prev => !prev)}
                  icon={
                    darkMode
                      ? <SunOutlined style={{ color: COLORS.gold }} />
                      : <MoonOutlined />
                  }
                />

              </Tooltip>


              {/* NOTIFICATIONS */}

              <Dropdown
                open={notificationOpen}
                onOpenChange={setNotificationOpen}
                placement="bottomRight"
                trigger={["click"]}
                dropdownRender={() => (

                  <div
                    style={{
                      width: 340,
                      maxWidth: "90vw",
                      background: theme.card,
                      borderRadius: 16,
                      padding: 15,
                      boxShadow: "0 15px 40px rgba(0,0,0,.17)",
                      border: `1px solid ${theme.border}`,
                      maxHeight: 400,
                      overflowY: "auto",
                    }}
                  >

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: 14,
                      }}
                    >

                      <Typography
                        style={{
                          fontWeight: 900,
                          fontSize: 16,
                          color: theme.text,
                        }}
                      >
                        Notifications
                      </Typography>

                      <Chip
                        size="small"
                        label={`${unreadCount} unread`}
                        sx={{
                          background: theme.goldLight,
                          color: COLORS.navy,
                          fontWeight: 800,
                          fontSize: 10,
                        }}
                      />

                    </div>


                    {notifications.length > 0 ? (

                      notifications.map(item => (

                        <div
                          key={item.id}
                          className="parent-notification-item"
                          onClick={() => handleNotificationClick(item.id)}
                          style={{
                            padding: "12px 11px",
                            borderRadius: 11,
                            cursor: item.is_read ? "default" : "pointer",
                            marginBottom: 8,
                            background: item.is_read
                              ? "transparent"
                              : darkMode
                                ? "#263F59"
                                : "#FFF8E5",
                            border: `1px solid ${
                              item.is_read
                                ? theme.border
                                : theme.goldBorder
                            }`,
                            opacity: clickedNotificationId === item.id ? .65 : 1,
                          }}
                        >

                          <div
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 9,
                            }}
                          >

                            <BellOutlined
                              style={{
                                color: item.is_read
                                  ? theme.secondary
                                  : COLORS.orange,
                                marginTop: 3,
                              }}
                            />

                            <div style={{ flex: 1, minWidth: 0 }}>

                              <Typography
                                style={{
                                  fontWeight: item.is_read ? 500 : 800,
                                  fontSize: 13,
                                  color: theme.text,
                                  lineHeight: 1.5,
                                }}
                              >
                                {item.message}
                              </Typography>

                              <Text
                                style={{
                                  fontSize: 10,
                                  color: theme.secondary,
                                  display: "block",
                                  marginTop: 5,
                                }}
                              >
                                {item.created_at
                                  ? new Date(item.created_at).toLocaleString()
                                  : ""}
                              </Text>

                            </div>

                            {!item.is_read && (
                              <span
                                style={{
                                  width: 7,
                                  height: 7,
                                  borderRadius: "50%",
                                  background: COLORS.orange,
                                  marginTop: 5,
                                  flexShrink: 0,
                                }}
                              />
                            )}

                          </div>

                        </div>

                      ))

                    ) : (

                      <div
                        style={{
                          textAlign: "center",
                          padding: "30px 15px",
                        }}
                      >

                        <BellOutlined
                          style={{
                            fontSize: 32,
                            color: theme.secondary,
                            marginBottom: 10,
                          }}
                        />

                        <Typography
                          style={{
                            color: theme.secondary,
                            fontSize: 13,
                          }}
                        >
                          No notifications available.
                        </Typography>

                      </div>

                    )}

                  </div>

                )}
              >

                <Badge
                  count={unreadCount}
                  offset={[-2, 3]}
                  size="small"
                >

                  <Button
                    className="parent-header-button"
                    shape="circle"
                    icon={<NotificationOutlined />}
                  />

                </Badge>

              </Dropdown>


              {/* LOGOUT */}

              <Button
                danger
                shape={isMobile ? "circle" : "default"}
                icon={<LogoutOutlined />}
                loading={loadingLogout}
                onClick={handleLogout}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 7,
                  fontWeight: 700,
                  borderRadius: 9,
                  height: 38,
                }}
              >
                {!isMobile && "Logout"}
              </Button>

            </div>

          </Header>


          {/* ==================================================
              MAIN CONTENT
          ================================================== */}

          <Content
            style={{
              padding: isMobile ? 12 : 25,
              background: theme.background,
              height: "calc(100vh - 75px)",
              overflowY: "auto",
              overflowX: "hidden",
              minWidth: 0,
            }}
          >

            <motion.div
              initial={{
                opacity: 0,
                y: 18,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                duration: .4,
              }}
            >


              {/* ==================================================
                  MAIN DASHBOARD
              ================================================== */}

              {activePage === "dashboard" && (

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: {
                      xs: 2.5,
                      md: 3.5,
                    },
                    maxWidth: 1600,
                    mx: "auto",
                  }}
                >

                  {/* DASHBOARD BANNER */}

                  <Box>

                    <BioLinkProfile
                      user={user}
                      dashboardBanner={dashboardBanner}
                    />

                  </Box>


                  {/* DASHBOARD OVERVIEW */}

                  <Box
                    sx={{
                      background: theme.card,
                      borderRadius: {
                        xs: 3,
                        md: 4,
                      },
                      padding: {
                        xs: 2,
                        md: 3.5,
                      },
                      boxShadow: darkMode
                        ? "0 12px 30px rgba(0,0,0,.12)"
                        : COLORS.shadow,
                      border: `1px solid ${theme.border}`,
                    }}
                  >

                    {/* SECTION HEADER */}

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: {
                          xs: "flex-start",
                          sm: "center",
                        },
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 2,
                        mb: 3,
                      }}
                    >

                      <Box>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 0.8,
                          }}
                        >

                          <Box
                            sx={{
                              width: 4,
                              height: 25,
                              borderRadius: 5,
                              background: COLORS.gold,
                            }}
                          />

                          <Typography
                            sx={{
                              fontWeight: 900,
                              fontSize: {
                                xs: 20,
                                md: 24,
                              },
                              color: theme.text,
                            }}
                          >
                            Parent Dashboard
                          </Typography>

                        </Box>

                        <Typography
                          sx={{
                            color: theme.secondary,
                            fontSize: 13,
                            lineHeight: 1.7,
                            pl: 1.5,
                          }}
                        >
                          Access your children's academic information,
                          assignments and school activities.
                        </Typography>

                      </Box>


                      <Chip
                        icon={
                          <SafetyCertificateOutlined
                            style={{
                              color: COLORS.orange,
                              fontSize: 15,
                            }}
                          />
                        }
                        label="Parent Portal"
                        sx={{
                          background: theme.goldLight,
                          color: COLORS.navy,
                          fontWeight: 800,
                          border: `1px solid ${theme.goldBorder}`,
                          borderRadius: 2,
                          fontSize: 11,
                        }}
                      />

                    </Box>


                    {/* FEATURE CARDS */}

                    <Grid
                      container
                      spacing={2.5}
                    >

                      {/* ACADEMIC PROGRESS */}

                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                      >

                        <FeatureCard
                          icon={<FaGraduationCap />}
                          title="Academic Progress"
                          description="Monitor student performance, academic results and learning progress throughout the school year."
                          iconColor={COLORS.navyBlue}
                        />

                      </Grid>


                      {/* ASSIGNMENTS */}

                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                      >

                        <FeatureCard
                          icon={<ReadOutlined />}
                          title="Assignments"
                          description="Keep track of school assignments, homework and important learning activities."
                          iconColor={COLORS.blue}
                        />

                      </Grid>


                      {/* SCHOOL EVENTS */}

                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                      >

                        <FeatureCard
                          icon={<CalendarOutlined />}
                          title="School Events"
                          description="Stay informed about upcoming school activities, programs and important announcements."
                          iconColor={COLORS.orange}
                        />

                      </Grid>

                    </Grid>

                  </Box>


                  {/* CHILDREN OVERVIEW */}

                  <Box
                    sx={{
                      background: theme.card,
                      border: `1px solid ${theme.border}`,
                      borderRadius: 4,
                      p: {
                        xs: 2,
                        md: 3,
                      },
                      boxShadow: darkMode
                        ? "0 10px 25px rgba(0,0,0,.12)"
                        : COLORS.shadow,
                    }}
                  >

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 1,
                        mb: 2.5,
                      }}
                    >

                      <Box>

                        <Typography
                          sx={{
                            fontWeight: 900,
                            fontSize: 19,
                            color: theme.text,
                          }}
                        >
                          My Children
                        </Typography>

                        <Typography
                          sx={{
                            fontSize: 12,
                            color: theme.secondary,
                            mt: .5,
                          }}
                        >
                          Select a student to view their school records.
                        </Typography>

                      </Box>

                      <Chip
                        label={`${children.length} ${
                          children.length === 1 ? "Student" : "Students"
                        }`}
                        sx={{
                          background: theme.goldLight,
                          color: COLORS.navy,
                          fontWeight: 800,
                          border: `1px solid ${theme.goldBorder}`,
                        }}
                      />

                    </Box>


                    {loading ? (

                      <Box
                        sx={{
                          textAlign: "center",
                          py: 4,
                          color: theme.secondary,
                        }}
                      >
                        Loading your student information...
                      </Box>

                    ) : children.length > 0 ? (

                      <Grid
                        container
                        spacing={2}
                      >

                        {children.map(child => (

                          <Grid
                            item
                            xs={12}
                            sm={6}
                            md={4}
                            key={child.id}
                          >

                            <Box
                              onClick={() => {
                                setSelectedChild(child);
                                setActivePage(`child_${child.id}`);
                                setShowGrades(false);
                              }}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                padding: 2,
                                borderRadius: 3,
                                border: `1px solid ${theme.border}`,
                                background: darkMode
                                  ? "#102B43"
                                  : "#F8FAFC",
                                cursor: "pointer",
                                transition: "all .25s ease",
                                "&:hover": {
                                  borderColor: COLORS.gold,
                                  transform: "translateY(-3px)",
                                  boxShadow: "0 8px 20px rgba(3,27,53,.08)",
                                },
                              }}
                            >

                              <Avatar
                                src={
                                  child.student_admission_image ||
                                  defaultAvatar
                                }
                                sx={{
                                  width: 52,
                                  height: 52,
                                  border: `2px solid ${COLORS.gold}`,
                                }}
                              />

                              <Box sx={{ flex: 1, minWidth: 0 }}>

                                <Typography
                                  sx={{
                                    color: theme.text,
                                    fontWeight: 800,
                                    fontSize: 14,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {child.full_name}
                                </Typography>

                                <Typography
                                  sx={{
                                    color: theme.secondary,
                                    fontSize: 11,
                                    mt: .3,
                                  }}
                                >
                                  {child.grade_class_name || "Class not available"}
                                </Typography>

                              </Box>

                              <ArrowRightOutlined
                                style={{
                                  color: COLORS.orange,
                                  fontSize: 17,
                                }}
                              />

                            </Box>

                          </Grid>

                        ))}

                      </Grid>

                    ) : (

                      <Box
                        sx={{
                          textAlign: "center",
                          py: 4,
                          px: 2,
                          borderRadius: 3,
                          background: darkMode
                            ? "#102B43"
                            : "#F8FAFC",
                        }}
                      >

                        <UserOutlined
                          style={{
                            fontSize: 32,
                            color: theme.secondary,
                            marginBottom: 10,
                          }}
                        />

                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: theme.text,
                            mb: .5,
                          }}
                        >
                          No Linked Students
                        </Typography>

                        <Typography
                          sx={{
                            fontSize: 12,
                            color: theme.secondary,
                          }}
                        >
                          No students are currently linked to your account.
                        </Typography>

                      </Box>

                    )}

                  </Box>

                </Box>

              )}


              {/* ==================================================
                  ASSIGNMENTS PLACEHOLDER
              ================================================== */}

              {activePage === "assignments" && (

                <Box
                  sx={{
                    background: theme.card,
                    border: `1px solid ${theme.border}`,
                    borderRadius: 4,
                    padding: {
                      xs: 3,
                      md: 5,
                    },
                    textAlign: "center",
                    boxShadow: COLORS.shadow,
                  }}
                >

                  <Box
                    sx={{
                      width: 75,
                      height: 75,
                      borderRadius: 3,
                      background: theme.goldLight,
                      border: `1px solid ${theme.goldBorder}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: 2,
                    }}
                  >

                    <BookOutlined
                      style={{
                        fontSize: 34,
                        color: COLORS.navyBlue,
                      }}
                    />

                  </Box>

                  <Typography
                    sx={{
                      fontSize: 23,
                      fontWeight: 900,
                      color: theme.text,
                    }}
                  >
                    School Assignments
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 13,
                      color: theme.secondary,
                      mt: 1,
                    }}
                  >
                    Keep track of your children's homework and school assignments.
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 12,
                      color: theme.secondary,
                      mt: 3,
                    }}
                  >
                    Assignment content can be connected here when its page and API are available.
                  </Typography>

                </Box>

              )}


              {/* ==================================================
                  SELECTED CHILD PROFILE
              ================================================== */}

              {activePage.startsWith("child_") && selectedChild && (

                <Box
                  sx={{
                    background: theme.card,
                    borderRadius: 4,
                    padding: {
                      xs: 2,
                      md: 4,
                    },
                    border: `1px solid ${theme.border}`,
                    boxShadow: "0 15px 35px rgba(3,27,53,.07)",
                    maxWidth: 1500,
                    mx: "auto",
                  }}
                >

                  {/* CHILD HEADER */}

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: {
                        xs: "column",
                        md: "row",
                      },
                      gap: 3,
                      alignItems: "center",
                      mb: 4,
                      pb: 3,
                      borderBottom: `1px solid ${theme.border}`,
                    }}
                  >

                    <Avatar
                      src={
                        selectedChild.student_admission_image ||
                        defaultAvatar
                      }
                      sx={{
                        width: 145,
                        height: 145,
                        border: `5px solid ${COLORS.gold}`,
                        boxShadow: "0 12px 30px rgba(3,27,53,.16)",
                      }}
                    />


                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        textAlign: {
                          xs: "center",
                          md: "left",
                        },
                      }}
                    >

                      <Typography
                        sx={{
                          fontSize: {
                            xs: 24,
                            md: 32,
                          },
                          fontWeight: 900,
                          color: theme.text,
                        }}
                      >
                        {selectedChild.full_name}
                      </Typography>


                      <Typography
                        sx={{
                          color: theme.secondary,
                          fontSize: 14,
                          mt: .5,
                        }}
                      >
                        Student Profile
                      </Typography>


                      <Chip
                        label={selectedChild.grade_class_name || "N/A"}
                        sx={{
                          mt: 1.5,
                          background: COLORS.deepNavy,
                          color: COLORS.white,
                          fontWeight: 800,
                          border: `1px solid ${COLORS.gold}`,
                          borderRadius: 2,
                        }}
                      />


                      <Box sx={{ mt: 2 }}>

                        <Button
                          type="primary"
                          onClick={() => setShowGrades(true)}
                          icon={<ReadOutlined />}
                          style={{
                            borderRadius: 9,
                            background: `linear-gradient(135deg,${COLORS.deepNavy},${COLORS.blue})`,
                            border: `1px solid ${COLORS.gold}`,
                            fontWeight: 800,
                            height: 42,
                            padding: "0 24px",
                            boxShadow: "0 6px 15px rgba(3,27,53,.15)",
                          }}
                        >
                          View Grades
                        </Button>

                      </Box>

                    </Box>

                  </Box>


                  {/* GRADES */}

                  {showGrades ? (

                    <ParentsStudentGrades
                      selectedChild={selectedChild}
                      onBack={() => setShowGrades(false)}
                    />

                  ) : (

                    <Grid
                      container
                      spacing={3}
                    >

                      {/* ATTENDANCE */}

                      <Grid
                        item
                        xs={12}
                        md={6}
                      >

                        <Box
                          sx={{
                            padding: 3,
                            borderRadius: 3,
                            background: darkMode
                              ? "#102B43"
                              : "#F8FAFC",
                            border: `1px solid ${theme.border}`,
                            height: "100%",
                          }}
                        >

                          <Typography
                            sx={{
                              fontWeight: 900,
                              fontSize: 18,
                              color: theme.text,
                              mb: 2,
                            }}
                          >
                            Attendance Summary
                          </Typography>


                          <Grid
                            container
                            spacing={1.5}
                          >

                            {/* PRESENT */}

                            <Grid
                              item
                              xs={4}
                            >

                              <Box
                                sx={{
                                  textAlign: "center",
                                  padding: {
                                    xs: 1,
                                    sm: 2,
                                  },
                                  borderRadius: 3,
                                  background: darkMode
                                    ? "#173D34"
                                    : "#DCFCE7",
                                  border: "1px solid #86CBA0",
                                }}
                              >

                                <Typography
                                  sx={{
                                    fontSize: 12,
                                    color: darkMode
                                      ? "#C7F4D8"
                                      : "#166534",
                                  }}
                                >
                                  Present
                                </Typography>

                                <Typography
                                  sx={{
                                    fontSize: 25,
                                    fontWeight: 900,
                                    color: darkMode
                                      ? "#86E5AC"
                                      : "#166534",
                                  }}
                                >
                                  {selectedChild.total_present || 0}
                                </Typography>

                              </Box>

                            </Grid>


                            {/* ABSENT */}

                            <Grid
                              item
                              xs={4}
                            >

                              <Box
                                sx={{
                                  textAlign: "center",
                                  padding: {
                                    xs: 1,
                                    sm: 2,
                                  },
                                  borderRadius: 3,
                                  background: darkMode
                                    ? "#46252C"
                                    : "#FEE2E2",
                                  border: "1px solid #E6A1A1",
                                }}
                              >

                                <Typography
                                  sx={{
                                    fontSize: 12,
                                    color: darkMode
                                      ? "#FFD1D1"
                                      : "#991B1B",
                                  }}
                                >
                                  Absent
                                </Typography>

                                <Typography
                                  sx={{
                                    fontSize: 25,
                                    fontWeight: 900,
                                    color: darkMode
                                      ? "#FFAAAA"
                                      : "#991B1B",
                                  }}
                                >
                                  {selectedChild.total_absent || 0}
                                </Typography>

                              </Box>

                            </Grid>


                            {/* LATE */}

                            <Grid
                              item
                              xs={4}
                            >

                              <Box
                                sx={{
                                  textAlign: "center",
                                  padding: {
                                    xs: 1,
                                    sm: 2,
                                  },
                                  borderRadius: 3,
                                  background: darkMode
                                    ? "#44391D"
                                    : "#FEF3C7",
                                  border: `1px solid ${theme.goldBorder}`,
                                }}
                              >

                                <Typography
                                  sx={{
                                    fontSize: 12,
                                    color: darkMode
                                      ? "#FCE6A4"
                                      : "#92400E",
                                  }}
                                >
                                  Late
                                </Typography>

                                <Typography
                                  sx={{
                                    fontSize: 25,
                                    fontWeight: 900,
                                    color: darkMode
                                      ? "#F5D477"
                                      : "#92400E",
                                  }}
                                >
                                  {selectedChild.total_late || 0}
                                </Typography>

                              </Box>

                            </Grid>

                          </Grid>

                        </Box>

                      </Grid>


                      {/* FEES */}

                      <Grid
                        item
                        xs={12}
                        md={6}
                      >

                        <Box
                          sx={{
                            padding: 3,
                            borderRadius: 3,
                            background: darkMode
                              ? "#102B43"
                              : "#F8FAFC",
                            border: `1px solid ${theme.border}`,
                            height: "100%",
                          }}
                        >

                          <Typography
                            sx={{
                              fontWeight: 900,
                              fontSize: 18,
                              color: theme.text,
                              mb: 2,
                            }}
                          >
                            Fees Information
                          </Typography>


                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mb: 2,
                              gap: 2,
                            }}
                          >

                            <Text style={{ color: theme.secondary }}>
                              Total Fee
                            </Text>

                            <Text
                              style={{
                                fontWeight: 800,
                                color: theme.text,
                              }}
                            >
                              ${selectedChild.total_fee || 0}
                            </Text>

                          </Box>


                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mb: 2,
                              gap: 2,
                            }}
                          >

                            <Text style={{ color: theme.secondary }}>
                              Amount Paid
                            </Text>

                            <Text
                              style={{
                                fontWeight: 800,
                                color: theme.text,
                              }}
                            >
                              ${selectedChild.amount_paid || 0}
                            </Text>

                          </Box>


                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >

                            <Text style={{ color: theme.secondary }}>
                              Balance
                            </Text>

                            <Text
                              style={{
                                fontWeight: 900,
                                color: COLORS.orange,
                              }}
                            >
                              ${selectedChild.balance_fee || 0}
                            </Text>

                          </Box>

                        </Box>

                      </Grid>


                      {/* COMPLAINTS */}

                      <Grid
                        item
                        xs={12}
                      >

                        <Box
                          sx={{
                            padding: 3,
                            borderRadius: 3,
                            background: darkMode
                              ? "#102B43"
                              : "#F8FAFC",
                            border: `1px solid ${theme.border}`,
                          }}
                        >

                          <Typography
                            sx={{
                              fontSize: 19,
                              fontWeight: 900,
                              color: theme.text,
                              mb: 2,
                            }}
                          >
                            Complaints
                          </Typography>


                          {selectedChild.complaints &&
                          selectedChild.complaints.length > 0 ? (

                            selectedChild.complaints.map(item => (

                              <Box
                                key={item.id}
                                sx={{
                                  padding: 2,
                                  borderRadius: 3,
                                  mb: 1.5,
                                  background: theme.card,
                                  border: `1px solid ${theme.border}`,
                                }}
                              >

                                <Typography
                                  sx={{
                                    fontWeight: 800,
                                    color: theme.text,
                                    mb: .5,
                                  }}
                                >
                                  {item.title}
                                </Typography>


                                <Typography
                                  sx={{
                                    color: theme.secondary,
                                    fontSize: 13,
                                    lineHeight: 1.7,
                                  }}
                                >
                                  {item.description}
                                </Typography>

                              </Box>

                            ))

                          ) : (

                            <Typography
                              sx={{
                                color: theme.secondary,
                                fontSize: 13,
                              }}
                            >
                              No complaints found.
                            </Typography>

                          )}

                        </Box>

                      </Grid>

                    </Grid>

                  )}

                </Box>

              )}

            </motion.div>

          </Content>

        </Layout>

      </Layout>

    </div>

  );

};


export default ParentsDashboard;
